import { useCallback, useEffect, useMemo, useState } from "react";
import {
    fetchOfftrackHistoryEvent,
    fetchOfftrackHistoryEvents,
    fetchOfftrackIncidents,
    fetchOfftrackOverview,
    fetchOfftrackDiagnostics,
} from "../../../lib/offtrackApi.js";

const POLL_MS = 5000;

const TABS = [
    { id: "online", label: "Online", icon: "users" },
    { id: "incidents", label: "Incidents", icon: "warning" },
    { id: "history", label: "Historie", icon: "history" },
    { id: "diagnostics", label: "Diagnose", icon: "diagnostics" },
    { id: "livetiming", label: "Livetiming", icon: "signal" },
    { id: "export", label: "Export", icon: "download" },
];

function Icon({ name, size = 20 }) {
    const common = {
        width: size,
        height: size,
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 2,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        "aria-hidden": true,
    };

    if (name === "users") {
        return (
            <svg {...common}>
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        );
    }

    if (name === "warning") {
        return (
            <svg {...common}>
                <path d="M10.3 2.9 1.8 17a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 2.9a2 2 0 0 0-3.4 0Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
            </svg>
        );
    }

    if (name === "history") {
        return (
            <svg {...common}>
                <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                <path d="M3 3v5h5" />
                <path d="M12 7v5l3 2" />
            </svg>
        );
    }

    if (name === "diagnostics") {
        return (
            <svg {...common}>
                <path d="M4 12h3l2-6 4 12 2-6h5" />
                <path d="M3 4v16h18" />
            </svg>
        );
    }

    if (name === "signal") {
        return (
            <svg {...common}>
                <path d="M4 20v-4" />
                <path d="M9 20v-8" />
                <path d="M14 20V8" />
                <path d="M19 20V4" />
            </svg>
        );
    }

    if (name === "download") {
        return (
            <svg {...common}>
                <path d="M12 3v12" />
                <path d="m7 10 5 5 5-5" />
                <path d="M5 21h14" />
            </svg>
        );
    }

    if (name === "clock") {
        return (
            <svg {...common}>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
            </svg>
        );
    }

    if (name === "refresh") {
        return (
            <svg {...common}>
                <path d="M20 11a8 8 0 1 0 2 5" />
                <path d="M20 4v7h-7" />
            </svg>
        );
    }

    return null;
}

function parseApiDate(value) {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
    const normalized = hasZone ? raw : `${raw.replace(" ", "T")}Z`;
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
}

function timeLabel(value, withDate = false) {
    const date = parseApiDate(value);
    if (!date) return "–";

    return new Intl.DateTimeFormat("de-DE", {
        ...(withDate ? { day: "2-digit", month: "2-digit", year: "numeric" } : {}),
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    }).format(date);
}

function raceDateLabel(value) {
    const date = parseApiDate(value);
    if (!date) return "Datum unbekannt";
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}

function sessionLabel(value) {
    const key = String(value || "").toUpperCase();
    if (key === "TEST_DRIVE") return "Test Drive";
    if (key === "PRACTICE") return "Practice";
    if (key === "QUALIFY") return "Qualy";
    if (key === "RACE") return "Race";
    return key && key !== "OTHER" ? key : "–";
}

function formatTrack(row) {
    const track = String(row?.track_name || "").trim();
    const config = String(row?.track_config || "").trim();
    if (!track) return "–";
    if (!config || track.toLowerCase().includes(config.toLowerCase())) return track;
    return `${track} · ${config}`;
}

function historyOptionLabel(event) {
    const track = formatTrack(event);
    const date = raceDateLabel(event?.race_started_at || event?.event_started_at || event?.last_seen_at);
    return `${track} · ${date}`;
}

function EmptyState({ icon, title, text }) {
    return (
        <div className="offtrack-empty">
            <div className="offtrack-empty__icon"><Icon name={icon} size={28} /></div>
            <h3>{title}</h3>
            <p>{text}</p>
        </div>
    );
}

function StatusDot({ online = true }) {
    return <span className={`offtrack-dot${online ? " offtrack-dot--online" : ""}`} aria-hidden="true" />;
}

function LoadingRows({ columns = 6 }) {
    return Array.from({ length: 4 }, (_, idx) => (
        <tr key={`loading-${idx}`} className="offtrack-loading-row">
            {Array.from({ length: columns }, (__, cell) => (
                <td key={cell}><span className="offtrack-skeleton" /></td>
            ))}
        </tr>
    ));
}

function HistorySummary({ event, details }) {
    const driverCount = Number(details?.driver_count ?? event?.driver_count ?? 0);
    const raceOfftracks = Number(details?.race_offtracks ?? event?.race_offtracks ?? 0);
    const incomplete = Number(details?.incomplete_driver_count ?? event?.incomplete_driver_count ?? 0);

    return (
        <div className="offtrack-history-summary">
            <div className="offtrack-history-summary__item">
                <span>Strecke</span>
                <strong>{formatTrack(details?.event || event)}</strong>
            </div>
            <div className="offtrack-history-summary__item">
                <span>Fahrer</span>
                <strong>{driverCount}</strong>
            </div>
            <div className="offtrack-history-summary__item offtrack-history-summary__item--accent">
                <span>Race Offtracks</span>
                <strong>{raceOfftracks}</strong>
            </div>
            <div className="offtrack-history-summary__item">
                <span>Unvollständig</span>
                <strong>{incomplete}</strong>
            </div>
        </div>
    );
}

export default function OfftrackManagement() {
    const [activeTab, setActiveTab] = useState("online");
    const [overview, setOverview] = useState(null);
    const [incidents, setIncidents] = useState(null);
    const [historyEvents, setHistoryEvents] = useState(null);
    const [selectedHistoryKey, setSelectedHistoryKey] = useState("");
    const [historyDetails, setHistoryDetails] = useState(null);
    const [diagnostics, setDiagnostics] = useState(null);
    const [diagnosticHours, setDiagnosticHours] = useState(24);
    const [diagnosticDriverId, setDiagnosticDriverId] = useState("");
    const [diagnosticSessionType, setDiagnosticSessionType] = useState("");
    const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
    const [loadingOverview, setLoadingOverview] = useState(true);
    const [loadingIncidents, setLoadingIncidents] = useState(true);
    const [loadingHistoryEvents, setLoadingHistoryEvents] = useState(false);
    const [loadingHistoryDetails, setLoadingHistoryDetails] = useState(false);
    const [error, setError] = useState("");
    const [lastRefresh, setLastRefresh] = useState(null);

    const loadOverview = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoadingOverview(true);
        try {
            const data = await fetchOfftrackOverview();
            setOverview(data);
            setError("");
            setLastRefresh(new Date());
        } catch (err) {
            setError(String(err?.message || err || "Offtrack-Daten konnten nicht geladen werden."));
        } finally {
            if (!silent) setLoadingOverview(false);
        }
    }, []);

    const loadIncidents = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoadingIncidents(true);
        try {
            const data = await fetchOfftrackIncidents(48);
            setIncidents(data);
            setError("");
            setLastRefresh(new Date());
        } catch (err) {
            setError(String(err?.message || err || "Incident-Daten konnten nicht geladen werden."));
        } finally {
            if (!silent) setLoadingIncidents(false);
        }
    }, []);

    const loadHistoryEvents = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoadingHistoryEvents(true);
        try {
            const data = await fetchOfftrackHistoryEvents();
            const events = Array.isArray(data?.events) ? data.events : [];
            setHistoryEvents(data);
            setSelectedHistoryKey((current) => {
                if (current && events.some((event) => event.event_group_key === current)) return current;
                return events[0]?.event_group_key || "";
            });
            if (!events.length) setHistoryDetails(null);
            setError("");
        } catch (err) {
            setError(String(err?.message || err || "Historische Rennen konnten nicht geladen werden."));
        } finally {
            if (!silent) setLoadingHistoryEvents(false);
        }
    }, []);

    const loadHistoryDetails = useCallback(async (eventGroupKey, { silent = false } = {}) => {
        const key = String(eventGroupKey || "").trim();
        if (!key) {
            setHistoryDetails(null);
            return;
        }
        if (!silent) setLoadingHistoryDetails(true);
        try {
            const data = await fetchOfftrackHistoryEvent(key);
            setHistoryDetails(data);
            setError("");
        } catch (err) {
            setError(String(err?.message || err || "Historische Offtrack-Daten konnten nicht geladen werden."));
        } finally {
            if (!silent) setLoadingHistoryDetails(false);
        }
    }, []);

    const loadDiagnostics = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoadingDiagnostics(true);
        try {
            const data = await fetchOfftrackDiagnostics({
                hours: diagnosticHours,
                driverId: diagnosticDriverId,
                sessionType: diagnosticSessionType,
                limit: 700,
            });
            setDiagnostics(data);
            setError("");
            setLastRefresh(new Date());
        } catch (err) {
            setError(String(err?.message || err || "Diagnosedaten konnten nicht geladen werden."));
        } finally {
            if (!silent) setLoadingDiagnostics(false);
        }
    }, [diagnosticDriverId, diagnosticHours, diagnosticSessionType]);

    const refreshLive = useCallback(async ({ silent = false } = {}) => {
        await Promise.all([
            loadOverview({ silent }),
            loadIncidents({ silent }),
        ]);
    }, [loadIncidents, loadOverview]);

    const refreshCurrent = useCallback(async () => {
        await refreshLive();
        if (activeTab === "history") {
            await loadHistoryEvents();
            if (selectedHistoryKey) await loadHistoryDetails(selectedHistoryKey);
        }
        if (activeTab === "diagnostics") {
            await loadDiagnostics();
        }
    }, [activeTab, loadDiagnostics, loadHistoryDetails, loadHistoryEvents, refreshLive, selectedHistoryKey]);

    useEffect(() => {
        refreshLive();
        const timer = window.setInterval(() => refreshLive({ silent: true }), POLL_MS);
        return () => window.clearInterval(timer);
    }, [refreshLive]);

    useEffect(() => {
        if (activeTab === "history" && historyEvents === null) {
            loadHistoryEvents();
        }
    }, [activeTab, historyEvents, loadHistoryEvents]);

    useEffect(() => {
        if (activeTab === "history" && selectedHistoryKey) {
            loadHistoryDetails(selectedHistoryKey);
        }
    }, [activeTab, loadHistoryDetails, selectedHistoryKey]);

    useEffect(() => {
        if (activeTab !== "diagnostics") return undefined;
        loadDiagnostics();
        const timer = window.setInterval(() => loadDiagnostics({ silent: true }), POLL_MS);
        return () => window.clearInterval(timer);
    }, [activeTab, loadDiagnostics]);

    const onlineClients = Array.isArray(overview?.clients) ? overview.clients : [];
    const incidentDrivers = Array.isArray(incidents?.drivers) ? incidents.drivers : [];
    const historicalEvents = Array.isArray(historyEvents?.events) ? historyEvents.events : [];
    const historicalDrivers = Array.isArray(historyDetails?.drivers) ? historyDetails.drivers : [];
    const selectedHistoryEvent = historicalEvents.find((event) => event.event_group_key === selectedHistoryKey) || null;
    const diagnosticLogs = Array.isArray(diagnostics?.logs) ? diagnostics.logs : [];
    const diagnosticDrivers = Array.isArray(diagnostics?.drivers) ? diagnostics.drivers : [];
    const diagnosticMismatches = Array.isArray(diagnostics?.mismatches) ? diagnostics.mismatches : [];
    const diagnosticSummary = diagnostics?.summary || {};
    const latestVersion = overview?.latest_release?.version || "–";

    const exportDiagnosticsJson = useCallback(() => {
        if (!diagnostics) return;

        const payload = {
            exported_at: new Date().toISOString(),
            filters: {
                hours: diagnosticHours,
                driver_id: diagnosticDriverId || null,
                session_type: diagnosticSessionType || null,
            },
            summary: diagnosticSummary,
            mismatches: diagnosticMismatches,
            logs: diagnosticLogs,
        };

        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const driverPart = diagnosticDriverId ? `-driver-${diagnosticDriverId}` : "";
        const sessionPart = diagnosticSessionType ? `-${diagnosticSessionType.toLowerCase()}` : "";
        const stamp = new Date().toISOString().replace(/[:.]/g, "-");
        a.href = url;
        a.download = `racevision-offtrack-diagnostics${driverPart}${sessionPart}-${stamp}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }, [diagnostics, diagnosticDriverId, diagnosticHours, diagnosticLogs, diagnosticMismatches, diagnosticSessionType, diagnosticSummary]);

    const lastSyncLabel = useMemo(() => {
        if (overview?.last_sync_at) return timeLabel(overview.last_sync_at);
        if (lastRefresh) {
            return new Intl.DateTimeFormat("de-DE", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }).format(lastRefresh);
        }
        return "–";
    }, [lastRefresh, overview?.last_sync_at]);

    const refreshDisabled = loadingOverview || loadingIncidents || loadingHistoryEvents || loadingHistoryDetails || loadingDiagnostics;

    return (
        <div className="rv-page manager-modern-page offtrack-admin-page">
            <section className="rv-page-hero offtrack-admin-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Admin Bereich</div>
                        <h1 className="rv-page-title">Offtrack Verwaltung</h1>
                        <p className="rv-page-subtitle">
                            Verbundene Fahrer und echte 1x-Offtracks aus Test Drive, Practice, Qualy und Race live überwachen und historisch nach Rennen auswerten.
                        </p>
                    </div>
                    <div className="offtrack-hero-live">
                        <StatusDot online={Number(overview?.online_count || 0) > 0} />
                        <span>{Number(overview?.online_count || 0)} online</span>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}

            <section className="offtrack-admin-layout">
                <div className="rv-modern-card offtrack-main-card">
                    <div className="offtrack-tabs" role="tablist" aria-label="Offtrack Verwaltung">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === tab.id}
                                className={`offtrack-tab${activeTab === tab.id ? " offtrack-tab--active" : ""}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <Icon name={tab.icon} size={19} />
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    {activeTab === "online" && (
                        <div className="offtrack-tab-panel" role="tabpanel">
                            <div className="offtrack-section-head">
                                <div>
                                    <h2>Verbundene Fahrer</h2>
                                    <p>Fahrer mit aktuell laufendem RaceVision-Tool.</p>
                                </div>
                                <div className="offtrack-online-pill">
                                    <StatusDot />
                                    <span>{Number(overview?.online_count || 0)} online</span>
                                </div>
                            </div>

                            <div className="offtrack-table-wrap">
                                <table className="offtrack-table">
                                    <thead>
                                    <tr>
                                        <th>Fahrer</th>
                                        <th>Status</th>
                                        <th>Verbunden seit</th>
                                        <th>Session</th>
                                        <th>Offtracks</th>
                                        <th>Version</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loadingOverview && !overview ? (
                                        <LoadingRows columns={6} />
                                    ) : onlineClients.length ? (
                                        onlineClients.map((client) => (
                                            <tr key={client.client_id}>
                                                <td>
                                                    <div className="offtrack-driver-cell">
                                                        <StatusDot />
                                                        <div>
                                                            <strong>{client.driver_name || client.iracing_name || "Unbekannter Fahrer"}</strong>
                                                            {client.iracing_name && client.driver_name !== client.iracing_name && (
                                                                <small>{client.iracing_name}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td><span className="offtrack-status-badge">LIVE</span></td>
                                                <td>{timeLabel(client.connected_since_at)}</td>
                                                <td>
                                                    <div className="offtrack-session-cell">
                                                        <span>{sessionLabel(client.session_type)}</span>
                                                        <small>{formatTrack(client) !== "–" ? formatTrack(client) : "Programm verbunden"}</small>
                                                    </div>
                                                </td>
                                                <td className="offtrack-count-cell">{client.offtrack_count ?? "–"}</td>
                                                <td><span className="offtrack-version">v{client.app_version || "–"}</span></td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6">
                                                <EmptyState
                                                    icon="users"
                                                    title="Aktuell niemand online"
                                                    text="Sobald ein registrierter Fahrer das Offtrack Programm startet, erscheint er hier."
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === "incidents" && (
                        <div className="offtrack-tab-panel" role="tabpanel">
                            <div className="offtrack-section-head">
                                <div>
                                    <h2>Aktuelle Offtracks nach Session</h2>
                                    <p>Nur echte 1x-Offtracks. Test Drive, Practice, Qualy und Race werden getrennt gezählt und je Session bei 0 gestartet.</p>
                                </div>
                                <span className="offtrack-window-pill">letzte 48 h</span>
                            </div>

                            <div className="offtrack-table-wrap">
                                <table className="offtrack-table offtrack-incident-table">
                                    <thead>
                                    <tr>
                                        <th>Fahrer</th>
                                        <th>Strecke</th>
                                        <th>Test Drive</th>
                                        <th>Practice</th>
                                        <th>Qualy</th>
                                        <th>Race</th>
                                        <th>Aktuell</th>
                                        <th>Stand</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loadingIncidents && !incidents ? (
                                        <LoadingRows columns={8} />
                                    ) : incidentDrivers.length ? (
                                        incidentDrivers.map((driver) => (
                                            <tr key={`${driver.driver_id}:${driver.event_group_key || driver.last_seen_at || "event"}`}>
                                                <td>
                                                    <div className="offtrack-driver-cell">
                                                        <StatusDot online={Boolean(driver.online)} />
                                                        <div>
                                                            <strong>{driver.driver_name || driver.iracing_name || "Unbekannter Fahrer"}</strong>
                                                            <div className="offtrack-driver-meta">
                                                                {driver.online ? "online" : "offline"}
                                                                {driver.incomplete && (
                                                                    <span className="offtrack-incomplete" title="Das Programm wurde erst während einer laufenden Session gestartet.">
                                                                        unvollständig
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{formatTrack(driver)}</td>
                                                <td className="offtrack-session-count offtrack-session-count--test">{driver.test_drive ?? "–"}</td>
                                                <td className="offtrack-session-count">{driver.practice ?? "–"}</td>
                                                <td className="offtrack-session-count">{driver.qualify ?? "–"}</td>
                                                <td className="offtrack-session-count offtrack-session-count--race">{driver.race ?? "–"}</td>
                                                <td>{sessionLabel(driver.current_session_type)}</td>
                                                <td>{timeLabel(driver.last_seen_at, true)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8">
                                                <EmptyState
                                                    icon="warning"
                                                    title="Noch keine Sessiondaten"
                                                    text="Sobald Fahrer Test Drive, Practice, Qualy oder Race fahren, werden die Werte hier getrennt angezeigt."
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="offtrack-info-note">
                                <strong>Race bleibt erhalten:</strong> Ein Sessionwechsel setzt nur die sichtbare Zahl der neuen Session auf 0. Test Drive, Practice und Qualy bleiben separat gespeichert; für den späteren Export wird gezielt der Race-Wert verwendet.
                            </div>
                        </div>
                    )}

                    {activeTab === "history" && (
                        <div className="offtrack-tab-panel" role="tabpanel">
                            <div className="offtrack-section-head offtrack-history-head">
                                <div>
                                    <h2>Historische Renndaten</h2>
                                    <p>Wähle ein bereits erfasstes Hosted-Rennen aus und öffne die gespeicherten Practice-, Qualy- und Race-Offtracks aller Fahrer.</p>
                                </div>
                                <span className="offtrack-window-pill">{historicalEvents.length} Rennen</span>
                            </div>

                            <div className="offtrack-history-toolbar">
                                <label className="offtrack-history-field">
                                    <span>Rennen auswählen</span>
                                    <select
                                        className="rv-select offtrack-history-select"
                                        value={selectedHistoryKey}
                                        onChange={(event) => setSelectedHistoryKey(event.target.value)}
                                        disabled={loadingHistoryEvents || historicalEvents.length === 0}
                                    >
                                        {historicalEvents.length === 0 && <option value="">Keine Rennen vorhanden</option>}
                                        {historicalEvents.map((event) => (
                                            <option key={event.event_group_key} value={event.event_group_key}>
                                                {historyOptionLabel(event)}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            {loadingHistoryEvents && historyEvents === null ? (
                                <div className="offtrack-history-loading">
                                    <span className="offtrack-skeleton" />
                                    <span className="offtrack-skeleton" />
                                    <span className="offtrack-skeleton" />
                                </div>
                            ) : historicalEvents.length === 0 ? (
                                <EmptyState
                                    icon="history"
                                    title="Noch keine historischen Rennen"
                                    text="Sobald ein Hosted Event mindestens eine Race-Session erfasst hat, erscheint es automatisch in dieser Auswahl."
                                />
                            ) : (
                                <>
                                    <HistorySummary event={selectedHistoryEvent} details={historyDetails} />

                                    <div className="offtrack-history-caption">
                                        <div>
                                            <strong>{formatTrack(historyDetails?.event || selectedHistoryEvent)}</strong>
                                            <span>{raceDateLabel(historyDetails?.event?.race_started_at || selectedHistoryEvent?.race_started_at || selectedHistoryEvent?.last_seen_at)}</span>
                                        </div>
                                        {(historyDetails?.event?.iracing_subsession_id || selectedHistoryEvent?.iracing_subsession_id) ? (
                                            <small>SubSession #{historyDetails?.event?.iracing_subsession_id || selectedHistoryEvent?.iracing_subsession_id}</small>
                                        ) : null}
                                    </div>

                                    <div className="offtrack-table-wrap">
                                        <table className="offtrack-table offtrack-history-table">
                                            <thead>
                                            <tr>
                                                <th>Fahrer</th>
                                                <th>Practice</th>
                                                <th>Qualy</th>
                                                <th>Race</th>
                                                <th>Gesamt P/Q/R</th>
                                                <th>Datenstand</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {loadingHistoryDetails && !historyDetails ? (
                                                <LoadingRows columns={6} />
                                            ) : historicalDrivers.length ? (
                                                historicalDrivers.map((driver) => {
                                                    const total = Number(driver.practice || 0) + Number(driver.qualify || 0) + Number(driver.race || 0);
                                                    return (
                                                        <tr key={`${selectedHistoryKey}:${driver.driver_id}`}>
                                                            <td>
                                                                <div className="offtrack-driver-cell">
                                                                    <StatusDot online={false} />
                                                                    <div>
                                                                        <strong>{driver.driver_name || driver.iracing_name || "Unbekannter Fahrer"}</strong>
                                                                        <div className="offtrack-driver-meta">
                                                                            {driver.incomplete && (
                                                                                <span className="offtrack-incomplete" title="Mindestens eine Session wurde erst nach ihrem Start vom Programm erfasst.">
                                                                                    unvollständig
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="offtrack-session-count">{driver.practice ?? "–"}</td>
                                                            <td className="offtrack-session-count">{driver.qualify ?? "–"}</td>
                                                            <td className="offtrack-session-count offtrack-session-count--race">{driver.race ?? "–"}</td>
                                                            <td className="offtrack-history-total">{total}</td>
                                                            <td>{timeLabel(driver.last_seen_at, true)}</td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="6">
                                                        <EmptyState
                                                            icon="history"
                                                            title="Keine Fahrerdaten für dieses Rennen"
                                                            text="Für die ausgewählte Event-ID sind aktuell keine Offtrack-Sessions gespeichert."
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === "diagnostics" && (
                        <div className="offtrack-tab-panel" role="tabpanel">
                            <div className="offtrack-section-head">
                                <div>
                                    <h2>Diagnose & SDK-Logs</h2>
                                    <p>Zeigt nur relevante Zustandsänderungen und Entscheidungen des RaceVision-Tools – nicht jeden SDK-Frame.</p>
                                </div>
                                <span className="offtrack-window-pill">{diagnosticHours} Stunden</span>
                            </div>

                            <div className="offtrack-diagnostic-toolbar">
                                <label className="offtrack-history-field">
                                    <span>Zeitraum</span>
                                    <select className="rv-select" value={diagnosticHours} onChange={(event) => setDiagnosticHours(Number(event.target.value))}>
                                        <option value={6}>6 Stunden</option>
                                        <option value={24}>24 Stunden</option>
                                        <option value={48}>48 Stunden</option>
                                        <option value={168}>7 Tage</option>
                                    </select>
                                </label>
                                <label className="offtrack-history-field">
                                    <span>Fahrer</span>
                                    <select className="rv-select" value={diagnosticDriverId} onChange={(event) => setDiagnosticDriverId(event.target.value)}>
                                        <option value="">Alle Fahrer</option>
                                        {diagnosticDrivers.map((driver) => (
                                            <option key={driver.driver_id} value={driver.driver_id}>{driver.driver_name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="offtrack-history-field">
                                    <span>Session</span>
                                    <select className="rv-select" value={diagnosticSessionType} onChange={(event) => setDiagnosticSessionType(event.target.value)}>
                                        <option value="">Alle Sessions</option>
                                        <option value="TEST_DRIVE">Test Drive</option>
                                        <option value="PRACTICE">Practice</option>
                                        <option value="QUALIFY">Qualy</option>
                                        <option value="RACE">Race</option>
                                    </select>
                                </label>
                                <button type="button" className="btn btn--accent offtrack-diagnostic-refresh" onClick={() => loadDiagnostics()} disabled={loadingDiagnostics}>
                                    <Icon name="refresh" size={17} />
                                    <span>{loadingDiagnostics ? "Lade …" : "Diagnose laden"}</span>
                                </button>
                                <button type="button" className="btn" onClick={exportDiagnosticsJson} disabled={!diagnostics || loadingDiagnostics}>
                                    <Icon name="download" size={17} />
                                    <span>Log als JSON</span>
                                </button>
                            </div>

                            <div className="offtrack-diagnostic-summary">
                                <div><span>Logs</span><strong>{Number(diagnosticSummary.log_count || 0)}</strong></div>
                                <div><span>Verworfen</span><strong>{Number(diagnosticSummary.discarded_count || 0)}</strong></div>
                                <div className={Number(diagnosticSummary.rejected_count || 0) ? "is-warn" : ""}><span>Backend abgelehnt</span><strong>{Number(diagnosticSummary.rejected_count || 0)}</strong></div>
                                <div className={Number(diagnosticSummary.mismatch_count || 0) ? "is-danger" : ""}><span>Sync-Differenzen</span><strong>{Number(diagnosticSummary.mismatch_count || 0)}</strong></div>
                            </div>

                            {diagnosticMismatches.length > 0 && (
                                <div className="offtrack-diagnostic-alert">
                                    <strong>Client-/Server-Differenz erkannt</strong>
                                    <div className="offtrack-table-wrap">
                                        <table className="offtrack-table offtrack-diagnostic-mismatch-table">
                                            <thead><tr><th>Fahrer</th><th>Session</th><th>Strecke</th><th>Client</th><th>Server</th><th>Differenz</th><th>Stand</th></tr></thead>
                                            <tbody>
                                            {diagnosticMismatches.map((item) => (
                                                <tr key={item.session_id}>
                                                    <td><strong>{item.driver_name}</strong></td>
                                                    <td>{sessionLabel(item.session_type)}</td>
                                                    <td>{item.track_name || "–"}</td>
                                                    <td className="offtrack-session-count">{item.client_count}</td>
                                                    <td className="offtrack-session-count">{item.server_count}</td>
                                                    <td className="offtrack-diagnostic-diff">{item.difference > 0 ? `+${item.difference}` : item.difference}</td>
                                                    <td>{timeLabel(item.last_seen_at, true)}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <div className="offtrack-table-wrap">
                                <table className="offtrack-table offtrack-diagnostic-table">
                                    <thead>
                                    <tr>
                                        <th>Zeit</th><th>Fahrer</th><th>Session</th><th>Ereignis</th><th>Entscheidung</th><th>SDK</th><th>Incidents</th><th>Zähler</th><th>Grund</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {loadingDiagnostics && !diagnostics ? (
                                        <LoadingRows columns={9} />
                                    ) : diagnosticLogs.length ? (
                                        diagnosticLogs.map((log) => (
                                            <tr key={log.id} className={`offtrack-diagnostic-row offtrack-diagnostic-row--${String(log.level || "info").toLowerCase()}`}>
                                                <td>{timeLabel(log.occurred_at, true)}</td>
                                                <td><strong>{log.driver_name || log.iracing_name || "–"}</strong><small className="offtrack-diagnostic-version">v{log.app_version || "–"}</small></td>
                                                <td>{sessionLabel(log.session_type)}<small className="offtrack-diagnostic-track">{log.track_name || ""}</small></td>
                                                <td><span className="offtrack-diagnostic-kind">{log.kind}</span>{log.event_no ? <small>Event {log.event_no}</small> : null}</td>
                                                <td>{log.decision || "–"}{log.http_status ? <small>HTTP {log.http_status}</small> : null}</td>
                                                <td><code>raw={log.raw_flags ?? "–"}</code><small>rep={log.report ?? "–"} · pen={log.penalty ?? "–"}</small></td>
                                                <td>{log.incident_total_before ?? "–"} → {log.incident_total ?? "–"}</td>
                                                <td>{log.local_count ?? "–"}{log.server_offtrack_count !== null && log.server_offtrack_count !== undefined ? <small>Server {log.server_offtrack_count}</small> : null}</td>
                                                <td className="offtrack-diagnostic-reason">{log.reason || "–"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="9"><EmptyState icon="diagnostics" title="Keine Diagnosedaten" text="Sobald RaceVision-Tool 1.0.2 läuft, erscheinen hier SDK-Änderungen, Kandidatenentscheidungen und Übertragungsergebnisse." /></td></tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === "livetiming" && (
                        <div className="offtrack-tab-panel" role="tabpanel">
                            <EmptyState
                                icon="signal"
                                title="Livetiming vorbereitet"
                                text="Der Bereich ist bereits im Layout angelegt. Die Live-Timing-Daten werden in einer späteren Ausbaustufe angebunden."
                            />
                        </div>
                    )}

                    {activeTab === "export" && (
                        <div className="offtrack-tab-panel" role="tabpanel">
                            <EmptyState
                                icon="download"
                                title="Export vorbereitet"
                                text="Die Race-Offtracks werden bereits separat gespeichert. CSV- und weitere Exportfunktionen können hier später ergänzt werden."
                            />
                        </div>
                    )}
                </div>

                <aside className="offtrack-side-stack">
                    <div className="rv-modern-card offtrack-stat-card">
                        <div className="offtrack-stat-icon"><Icon name="users" size={25} /></div>
                        <div>
                            <span>Online Fahrer</span>
                            <strong>
                                <em>{Number(overview?.online_count || 0)}</em>
                                <b>/ {Number(overview?.registered_driver_count || 0)}</b>
                            </strong>
                            <small>Derzeit mit Offtrack verbunden.</small>
                        </div>
                    </div>

                    <div className="rv-modern-card offtrack-stat-card">
                        <div className="offtrack-stat-icon"><Icon name="signal" size={25} /></div>
                        <div>
                            <span>Aktive Sessions</span>
                            <strong><em>{Number(overview?.active_sessions || 0)}</em></strong>
                            <small>Test Drive, Practice, Qualy oder Race aktiv.</small>
                        </div>
                    </div>

                    <div className="rv-modern-card offtrack-stat-card">
                        <div className="offtrack-stat-icon"><Icon name="clock" size={25} /></div>
                        <div>
                            <span>Letzte Synchronisation</span>
                            <strong className="offtrack-stat-time"><em>{lastSyncLabel}</em></strong>
                            <small>Daten aktualisieren sich automatisch.</small>
                        </div>
                    </div>

                    <div className="rv-modern-card offtrack-stat-card">
                        <div className="offtrack-stat-icon"><Icon name="download" size={25} /></div>
                        <div>
                            <span>Aktuelle App-Version</span>
                            <strong className="offtrack-stat-version"><em>v{latestVersion}</em></strong>
                            <small>Clients prüfen automatisch auf Updates.</small>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="offtrack-refresh-button"
                        onClick={refreshCurrent}
                        disabled={refreshDisabled}
                    >
                        <Icon name="refresh" size={19} />
                        <span>{refreshDisabled ? "Aktualisiere …" : "Liste aktualisieren"}</span>
                    </button>
                </aside>
            </section>
        </div>
    );
}
